import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Baby, Target, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { BOOKING_URL } from '@/constants/urls';
import treatmentImage from '@/assets/treatment.jpg';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';

const MenopausePage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Chinese Medicine for Menopause Sunshine Coast | Hormonal Balance Support";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Supporting your next chapter with grace, strength, and steadiness. Chinese medicine care for menopause symptoms, hot flushes, and hormonal balance in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Chinese Medicine for Menopause Sunshine Coast | Hormonal Balance Support");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Supporting your next chapter with grace, strength, and steadiness. Chinese medicine care for menopause symptoms, hot flushes, and hormonal balance in Currimundi.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/menopause");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/treatment.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Chinese Medicine for Menopause Sunshine Coast | Hormonal Balance Support");
    }

    // Add LocalBusiness Schema for menopause services
    const menopauseSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Chinese Medicine for Menopause",
      "image": "https://womenandtheway.com.au/treatment.jpg",
      "description": "Supporting your next chapter with grace, strength, and steadiness. Chinese medicine care for menopause symptoms, hot flushes, and hormonal balance in Currimundi.",
      "url": "https://womenandtheway.com.au/menopause",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Menopause Care, Hormonal Balance, Acupuncture, Chinese Medicine",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(menopauseSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                  Chinese Medicine for Menopause
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Supporting your next chapter with grace, strength, and steadiness.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Menopause is a natural transition that every woman experiences, but it doesn't have to be a time of suffering. At Women and the Way, we honor this important life stage with gentle, supportive care that helps you navigate the changes with grace and vitality.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're experiencing hot flashes, sleep disturbances, mood changes, or simply want to support your health through this transition, our treatments help restore balance and energy. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of menopause physiology. Many of our clients also benefit from our <a href="/sleep-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">sleep support</a> and <a href="/emotional-healing" className="text-sage-600 hover:text-sage-800 underline transition-colors">emotional healing services</a> during this transition.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={treatmentImage} 
                  alt="Woman receiving gentle acupuncture for menopause support and hormonal balance" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Menopause Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Moon className="w-8 h-8 text-sage-600" />
                Why Menopause Support Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, menopause marks the transition from the reproductive phase (Heavenly Water) to the wise woman phase - guided by the Heart and Kidneys. As Yin and Jing naturally decline, symptoms such as hot flushes, night sweats, dryness, anxiety, and fatigue can arise when the body struggles to stay balanced.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                With the right support, this time of change can be one of renewed wisdom, clarity, and confidence. Our goal is to help you feel more like you again - comfortable in your body, calm in your mind, and supported in your daily life.
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Menopause Care In-Clinic or Online
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Visit our Currimundi clinic for restorative treatments designed to cool, nourish, and rebalance.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to calm hot flushes, reduce anxiety, and support energy and sleep</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine to support Yin, anchor the Heart, and ease dryness or fatigue</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💆‍♀️</span>
                      <p className="text-sage-700">Gentle bodywork and therapeutic massage to reduce muscle tension and emotional overwhelm</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Menopause mentoring and lifestyle support to help you thrive - not just survive</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Sessions</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Care from the comfort of home.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth sessions to explore your symptoms and create a personalised care plan</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Herbs posted to your door or picked up from our clinic</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📝</span>
                      <p className="text-sage-700">Guidance on cooling foods, warming rituals, and Yin-nourishing practices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* The Menopausal Journey */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                The Menopausal Journey
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Perimenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 The transitional years - fluctuating hormones, irregular cycles, emotional intensity</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Menopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 No period for 12+ months - symptoms like heat, dryness, anxiety, low libido</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Postmenopause</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 New equilibrium - support for bone health, memory, and mood still matters</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Common Symptoms We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Common Symptoms We Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Hot flushes & night sweats", chinese: "Yin deficiency, Empty Heat, Heart-Kidney disharmony" },
                { western: "Mood swings & irritability", chinese: "Liver Qi stagnation, Yin deficiency" },
                { western: "Dry skin, eyes, or vaginal tissue", chinese: "Kidney Yin deficiency, Blood dryness" },
                { western: "Low libido & discomfort", chinese: "Jing deficiency, Yin-Blood depletion" },
                { western: "Weight gain or sluggish metabolism", chinese: "Spleen Qi deficiency, Damp accumulation" },
                { western: "Brain fog or poor memory", chinese: "Kidney essence decline, Heart-Spleen imbalance" },
                { western: "Fatigue & poor sleep", chinese: "Yin deficiency, Heart Fire, Spleen Qi deficiency" },
                { western: "Anxiety or low mood", chinese: "Heart Yin deficiency, Shen disturbance" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Our Care Different */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Care Different
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "Compassionate, personalised care", desc: "Your menopause experience is unique. So is your care plan." },
                { icon: "🌿", title: "Ancient wisdom, modern relevance", desc: "We bring centuries of Chinese medicine into a format that works for your life" },
                { icon: "📚", title: "Real-world strategies", desc: "From acupressure and sleep rituals to dietary and supplement advice, we help you build support systems" },
                { icon: "👩‍🦳", title: "Wise woman care", desc: "This is a sacred life stage. We're here to help you step into it with confidence and connection" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Tools for Menopause Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Leaf, title: "Custom herbal formulas", desc: "Capsules, teas, or tinctures" },
                { icon: Coffee, title: "Cooling herbal teas", desc: "And Yin-nourishing tonics" },
                { icon: Heart, title: "Castor oil and heat therapy", desc: "Guidance for home use" },
                { icon: BookOpen, title: "Reading recommendations", desc: "Hormone Repair Manual, Second Spring, The Wisdom of Menopause" },
                { icon: Target, title: "Simple acupressure tools", desc: "For sleep, hot flushes, and mood" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Menopause Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Your hormones may be shifting, but your strength is not lost. Let's navigate this time together - with support that helps you feel balanced, steady, and beautifully at home in your body.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default MenopausePage; 